import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha, Chip } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LinhasEsqueletoTabela, paperListagemSx, tableContainerListagemSx } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { MovimentoService, IListagemMovimento } from '../../../shared/services/api/estoque/movimento/MovimentoService';
import { brand } from '../../../shared/themes';
import dayjs from 'dayjs';
import { FerramentasDaListagemEntrada } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemEntrada';
import ReceiptIcon from '@mui/icons-material/ReceiptRounded';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];


export const ListagemDeEntradas: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemMovimento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'entradas';
  // Altura fixa de uma linha: o Valor Total é uma linha só (não empilha mais um "Total" embaixo,
  // pra bater com o padrão único de coluna monetária das outras listagens), mais o padding
  // vertical (py: 1.25 => 20px) e a borda.
  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 48,
    footerReservado: 68,
    bottomGap: 24,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  const dataInicial = useMemo(() => {
    return searchParams.get('datainicial') || dayjs().startOf('month').format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const dataFinal = useMemo(() => {
    return searchParams.get('datafinal') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);
  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);
  const idparceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);
  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }
  const [busca, setBusca] = useState(true);
  const [autoComplete, setAutoComplete] = useState(true);
  const [data, setData] = useState(true);
  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setBusca(false);
      setAutoComplete(false);
      setRows([]);
      setSearchParams({ tipo: 'data', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'parceiro') {
      setAutoComplete(true);
      setBusca(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'parceiro', pagina: '1' }, { replace: true });
      setTotalCount(0);
    } else {
      setBusca(true);
      setAutoComplete(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'numero', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
  }, [tipo]);
  useEffect(() => {
    if (tipo === 'numero' && filter != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);

      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    }
  }, [filter, pagina, rowsPerPage]);
  useEffect(() => {
    // O "else" só deve limpar a listagem quando o tipo ativo É "data" mas o filtro de datas
    // está inválido — nunca quando o tipo ativo é outro (numero/parceiro). Antes o "else" era
    // avaliado sempre que tipo !== 'data' também, então toda vez que rowsPerPage/pagina mudava
    // (independente do tipo selecionado), este efeito zerava a listagem por cima do resultado
    // que o efeito do tipo realmente ativo estava buscando — causando o "pisca" de duas listagens.
    if (tipo !== 'data') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    // Mesmo motivo do efeito acima: só limpa a listagem quando o tipo ativo É "parceiro"
    // mas o parceiro está vazio, nunca quando outro tipo está ativo.
    if (tipo !== 'parceiro') return;

    if (idparceiro != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        MovimentoService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);

            }
          });
      });
    } else {
      setRows([]);
    }
  }, [idparceiro, pagina, tipo, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({
        parceiro: idparceiro,
        filter: filter,
        tipo: tipo,
        datainicial: dataInicial,
        datafinal: dataFinal,
        pagina: '1'
      }, { replace: true });
    }
  }, [rowsPerPage]);
  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';
    const apenasNumeros = documento.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documento;
  };

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8/refoco de busca aqui (omitindo tiposBusca/onTrocarTipo): ao contrário das outras
  // listagens, esta tela alterna entre 3 widgets de busca bem diferentes entre si (datas,
  // autocomplete de parceiro, texto de número) em vez de um único campo de texto — não há um
  // "input de busca" único pra focar. A navegação entre linhas (setas/Enter/pular de página)
  // continua funcionando normalmente.
  const { linhaRefCallback, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({
      parceiro: idparceiro, filter, tipo, datainicial: dataInicial, datafinal: dataFinal, pagina: novaPagina.toString(),
    }, { replace: true }),
    onEnter: (index) => navigate(`/entrada/${rows[index].idmov}`),
    rowsDep: [rows],
  });

  return (
    <>
      <LayoutBaseDePagina
        titulo='Entradas'
        subtitulo='Visualize, pesquise e gerencie todas as entradas de estoque cadastradas no sistema.'
        barraDeFerramentas={
          <FerramentasDaListagemEntrada
            mostrarInputBusca={busca}
            mostrarInputBasicSelect
            mostrarInputData={data}
            mostrarAutoComplete={autoComplete}
            dataInicial={dataInicial}
            dataFinal={dataFinal}
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Nova Entrada'
            aoClicarEmNovo={() => navigate('/entrada/novo')}
            aoMudarDataInicial={datainicial => setSearchParams({ tipo: tipo, pagina: '1', datainicial: datainicial, datafinal: dataFinal }, { replace: true })}
            aoMudarDataFinal={datafinal => setSearchParams({ tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: datafinal }, { replace: true })}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoParceiro={(texto, texto1) => setSearchParams({ parceiroselect: texto1, parceiro: texto, filter: filter, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
              { nameBD: 'parceiro', descricao: 'Parceiro' },
              { nameBD: 'numero', descricao: 'Número' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper elevation={0} sx={paperListagemSx(theme)}>
          <TableContainer
            ref={tabelaRef}
            sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
          >
            <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '110px' }}>Data</TableCell>
                  <TableCell>Parceiro</TableCell>
                  <TableCell sx={{ width: '150px' }}>Documento</TableCell>
                  <TableCell sx={{ width: '100px' }}>Nº Nota</TableCell>
                  <TableCell align='right' sx={{ width: '130px' }}>Valor Total</TableCell>
                </TableRow>
              </TableHead>

              {isLoading && rows.length === 0 && (
                <TableBody>
                  <LinhasEsqueletoTabela
                    colunas={[
                      { width: '70%' },
                      { width: '80%' },
                      { width: '75%' },
                      { width: '55%' },
                      { width: '65%', align: 'right' },
                    ]}
                  />
                </TableBody>
              )}
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.idmov}
                    ref={linhaRefCallback(index)}
                    hover
                    tabIndex={0}
                    sx={{
                      cursor: 'pointer',
                      outline: 'none',
                      '&:last-child td': { borderBottom: 0 },
                      '& td': {
                        py: 1.25,
                        px: 1.5,
                        fontSize: '0.8125rem',
                        borderBottom: `1px solid ${theme.torq.border.default}`,
                      },
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.035),
                      },
                      // Indicador visual de qual linha está focada — necessário porque a
                      // navegação por teclado (setas) move o foco entre as linhas.
                      '&:focus-visible': {
                        outline: `2px solid ${theme.torq.border.focus}`,
                        outlineOffset: '-2px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                    onClick={() => navigate(`/entrada/${row.idmov}`)}
                    onKeyDown={onLinhaKeyDown(index)}
                  >
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {dayjs(row.emissao).format('DD/MM/YYYY')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {row.nome || 'Não informado'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography fontFamily="monospace" noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                        {formatarDocumento(row.cpf || '')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={row.numero || 'N/A'}
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1.25 },
                          bgcolor: theme.torq.surface.level2,
                          color: theme.torq.text.secondary,
                        }}
                      />
                    </TableCell>

                    <TableCell align='right'>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: row.total >= 0 ? 'success.main' : 'error.main' }}>
                        {row.total.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {rows.length === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align='center' sx={{ py: 3 }}>
                      <Box>
                        <ReceiptIcon sx={{
                          fontSize: 35,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                          {filter || idparceiro ? 'Tente ajustar os filtros de busca' : 'Nenhuma entrada encontrada no período'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              <TableFooter>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0 }}>
                      <LinearProgress sx={{ height: '1.5px' }} />
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                      <Typography variant='caption' color='text.secondary'>
                        {totalCount === 0
                          ? 'Nenhuma entrada encontrada'
                          : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} entrada${totalCount !== 1 ? 's' : ''}`}
                      </Typography>

                      {totalCount > rowsPerPage && (
                        <Pagination
                          page={pagina}
                          count={totalPaginas}
                          onChange={(_, newPage) => setSearchParams({
                            parceiro: idparceiro,
                            filter: filter,
                            tipo: tipo,
                            datainicial: dataInicial,
                            datafinal: dataFinal,
                            pagina: newPage.toString()
                          }, { replace: true })}
                          size="small"
                          shape="rounded"
                          showFirstButton
                          showLastButton
                          siblingCount={1}
                          boundaryCount={1}
                          sx={{
                            '& .MuiPaginationItem-root.Mui-selected': {
                              backgroundColor: brand.menuBase,
                              color: '#fff',
                              '&:hover': { backgroundColor: '#012D3D' },
                            },
                          }}
                        />
                      )}

                      <FormControl size='small' sx={{ minWidth: 110 }}>
                        <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                          {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                            <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};